/**
 * Entry application component used to compose providers and render Routes.
 * */

import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { Routes } from "../app/Routes";
import { NotificationContainer } from "react-notifications";
import { I18nProvider } from "../_metronic/i18n";
import { LayoutSplashScreen, MaterialThemeProvider } from "../_metronic/layout";
import { Extensions } from "../utils/Extensions";
import { CommonContextProvider } from "../app/context/commonContext";
import AddCardForm from "../app/modules/payment/newPaymentPage";

Extensions();

// Component to hide splash screen for payment routes
function HideSplashScreen({ children }) {
  React.useEffect(() => {
    const splashScreen = document.getElementById("splash-screen");
    if (splashScreen) {
      splashScreen.classList.add("hidden");
    }
  }, []);

  return <>{children}</>;
}

export default function App({ store, persistor, basename }) {
  return (
    /* Override `basename` (e.g: `homepage` in `package.json`) */
    <BrowserRouter basename={basename}>
      <MaterialThemeProvider>
        <Switch>
          {/* Payment routes - rendered outside heavy contexts for fast loading */}
          <Route path="/payment" render={() => <HideSplashScreen><AddCardForm variant="prod" isThirdParty={false} /></HideSplashScreen>} />
          <Route path="/thirdpartypayment" render={() => <HideSplashScreen><AddCardForm variant="prod" isThirdParty={true} /></HideSplashScreen>} />

          <Route path="/devpayment" render={() => <HideSplashScreen><AddCardForm variant="dev" isThirdParty={false} /></HideSplashScreen>} />
          <Route path="/thirdpartydevpayment" render={() => <HideSplashScreen><AddCardForm variant="dev" isThirdParty={true} /></HideSplashScreen>} />

          <Route path="/localpayment" render={() => <HideSplashScreen><AddCardForm variant="local" isThirdParty={false} /></HideSplashScreen>} />
          <Route path="/thirdpartylocalpayment" render={() => <HideSplashScreen><AddCardForm variant="local" isThirdParty={true} /></HideSplashScreen>} />

          {/* All other routes wrapped in full context providers */}
          <Route path="*">
            {/* Provide Redux store */}
            <Provider store={store}>
              {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
              <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
                {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
                <React.Suspense fallback={<LayoutSplashScreen />}>
                  {/* Provide `react-intl` context synchronized with Redux state.  */}
                  <I18nProvider>
                    <CommonContextProvider>
                      <NotificationContainer />
                      {/* Render routes with provided `Layout`. */}
                      <Routes />
                    </CommonContextProvider>
                  </I18nProvider>
                </React.Suspense>
              </PersistGate>
            </Provider>
          </Route>
        </Switch>
      </MaterialThemeProvider>
    </BrowserRouter>
  );
}
